import React, { useEffect, useState } from 'react';
import { adminStaffingAPI } from '@/services/staffingAPI';
import { Check, Ban, RefreshCw, Loader2, Building2, BadgeCheck, ShieldAlert } from 'lucide-react';
import Pagination, { usePagination } from '@/components/shared/Pagination';
import ScrollHint from '@/components/shared/ScrollHint';

const STATUS_TABS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'revoked', label: 'Revoked' },
  { value: 'all', label: 'All' },
];

export default function AdminStaffingOrgs() {
  const [tab, setTab] = useState('pending');
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({});
  const p = usePagination(orgs, 20);

  const load = async () => {
    setLoading(true);
    try { const d = await adminStaffingAPI.listOrgs({ status: tab, limit: 500 }); setOrgs(d.orgs || []); }
    catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [tab]);

  const act = async (action, id) => {
    setBusy({...busy, [id]: action});
    try {
      if (action === 'approve') await adminStaffingAPI.approveOrg(id);
      if (action === 'revoke') await adminStaffingAPI.revokeOrg(id);
      if (action === 'restore') await adminStaffingAPI.restoreOrg(id);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Action failed'); }
    setBusy({...busy, [id]: null});
  };

  return (
    <div data-testid="admin-staffing-orgs">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1 flex items-center gap-2"><Building2 className="w-5 h-5" /> Staffing Organizations</h1>
      <p className="text-slate-500 dark:text-zinc-500 text-sm mb-6">Approve, revoke, or restore staffing partners onboarded to Numuv.</p>

      <div className="flex gap-2 mb-4">
        {STATUS_TABS.map(t => (
          <button key={t.value} onClick={() => setTab(t.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium ${tab === t.value ? 'bg-slate-900 dark:bg-white text-white dark:text-black' : 'bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400 hover:bg-slate-200 dark:hover:bg-zinc-700'}`}
            data-testid={`tab-${t.value}`}>
            {t.label}
          </button>
        ))}
      </div>

      <ScrollHint>
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/60 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-white dark:bg-[#0e0e10] text-slate-500 dark:text-zinc-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-3">Org Name</th>
              <th className="text-left p-3">Domain</th>
              <th className="text-left p-3">Head</th>
              <th className="text-left p-3">Members</th>
              <th className="text-left p-3">Candidates</th>
              <th className="text-left p-3">Balance</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-6 text-center text-slate-500 dark:text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></td></tr>}
            {!loading && orgs.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500 dark:text-zinc-500">No staffing orgs in this state.</td></tr>}
            {p.pageItems.map(o => (
              <tr key={o.id} className="border-t border-slate-200 dark:border-zinc-800/50" data-testid={`staffing-row-${o.id}`}>
                <td className="p-3 text-slate-900 dark:text-white">{o.name}</td>
                <td className="p-3 text-slate-500 dark:text-zinc-400 text-xs">@{o.domain}</td>
                <td className="p-3 text-slate-500 dark:text-zinc-400 text-xs">
                  <div className="flex items-center gap-1.5">
                    {o.head_email}
                    {o.head_email_verified ? (
                      <span title="Head email verified" data-testid={`head-verified-${o.id}`} className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400"><BadgeCheck className="w-3 h-3" /></span>
                    ) : (
                      <span title="Head email not verified" data-testid={`head-unverified-${o.id}`} className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400"><ShieldAlert className="w-3 h-3" /></span>
                    )}
                  </div>
                </td>
                <td className="p-3 text-slate-600 dark:text-zinc-300">{o.members_count}</td>
                <td className="p-3 text-slate-600 dark:text-zinc-300">{o.candidates_count}</td>
                <td className={`p-3 ${o.head_balance >= 0 ? 'text-slate-600 dark:text-zinc-300' : 'text-red-700 dark:text-red-300'}`}>{o.head_balance} MP</td>
                <td className="p-3 text-right space-x-2 whitespace-nowrap">
                  {!o.org_approved && o.domain_status !== 'revoked' && (
                    <button onClick={() => act('approve', o.id)} disabled={busy[o.id]} className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 text-xs inline-flex items-center gap-1" data-testid={`approve-${o.id}`}><Check className="w-3 h-3" /> Approve</button>
                  )}
                  {o.org_approved && o.domain_status !== 'revoked' && (
                    <button onClick={() => act('revoke', o.id)} disabled={busy[o.id]} className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-xs inline-flex items-center gap-1" data-testid={`revoke-${o.id}`}><Ban className="w-3 h-3" /> Revoke</button>
                  )}
                  {o.domain_status === 'revoked' && (
                    <button onClick={() => act('restore', o.id)} disabled={busy[o.id]} className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 text-xs inline-flex items-center gap-1" data-testid={`restore-${o.id}`}><RefreshCw className="w-3 h-3" /> Restore</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </ScrollHint>
      <Pagination {...p.paginationProps} testIdPrefix="admin-staffing-pg" />
    </div>
  );
}
